import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit,
  Output, ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { MenuService } from '../../core/services/menu.service';
import { MenuNode } from '../../core/models/menu.model';

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input()  collapsed   = false;
  @Input()  mobileOpen  = false;
  @Output() toggle      = new EventEmitter<void>();
  @Output() mobileClose = new EventEmitter<void>();

  /** The scrollable nav container — used to bring the active item into view. */
  @ViewChild('sidebarNav') private sidebarNav?: ElementRef<HTMLElement>;
  private viewReady = false;

  /** Top-level tree nodes bound to the template. */
  menuNodes: MenuNode[] = [];
  menuLoading = false;

  /**
   * The single most-specific menu route that matches the current URL. Only the
   * leaf owning this exact path (and its ancestors) is highlighted, so nested
   * sibling routes like /employees and /employees/details-import never both
   * light up.
   */
  private activeRoutePath: string | null = null;
  /** The active route from the previous render — used to detect real navigation. */
  private previousActiveRoutePath: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly auth:        AuthService,
    readonly menuService: MenuService,
    private  router:      Router,
  ) {}

  ngOnInit(): void {
    // Subscribe to the menu tree.
    this.menuService.menuNodes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(nodes => {
        this.menuNodes = nodes;
        this.expandActiveAncestors(nodes);
        // After the menu renders on load/refresh, bring the active item into
        // view so a selection far down the list is visible without scrolling.
        this.scrollActiveIntoView();
      });

    this.menuService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => this.menuLoading = loading);

    // When the route changes, re-expand ancestors of the now-active item.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.expandActiveAncestors(this.menuNodes);
      this.scrollActiveIntoView();
    });

    // Explicit initial load. The reactive combineLatest in MenuService can
    // miss the very first emission depending on when the singleton was
    // constructed relative to login. Trigger an initial load here so the
    // sidebar always fetches menus after login and after a page refresh.
    if (this.auth.isAuthenticated() && this.menuNodes.length === 0) {
      const compCode = this.auth.getSelectedCompany()?.compCode ?? '';
      this.menuService.loadMenus(compCode);
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    // Initial render — scroll to the active route's menu item (e.g. after a
    // page refresh where the active item sits below the fold).
    this.scrollActiveIntoView();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Scroll the currently-active menu item into view within the nav. Runs after
   * menu load, navigation and initial render. Skipped while the sidebar is
   * collapsed (items are icon-only and the list is short).
   */
  private scrollActiveIntoView(): void {
    if (!this.viewReady || this.collapsed) return;
    // Defer so expansion (.nav-children max-height, ~0.3s) and [class.active]
    // bindings have settled before measuring, so the item lands at its final
    // position rather than mid-animation.
    setTimeout(() => {
      const nav = this.sidebarNav?.nativeElement;
      if (!nav) return;
      // Prefer the active leaf (<a.nav-item.active>); fall back to any active item.
      const active =
        nav.querySelector<HTMLElement>('a.nav-item.active') ??
        nav.querySelector<HTMLElement>('.nav-item.active');
      active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, 320);
  }

  // ── Expand/collapse ────────────────────────────────────────────────────────

  toggleNode(node: MenuNode, event: Event): void {
    if (node.children.length === 0) return;
    event.preventDefault();
    // Only toggle the clicked section. Opening one section does NOT collapse
    // any other open section — sections stay open until the user closes them
    // or navigates to a menu in a different section.
    node.expanded = !node.expanded;
  }

  /**
   * Recursively expands every ancestor of the currently active route.
   * Called after menu load and on every NavigationEnd event.
   */
  private expandActiveAncestors(nodes: MenuNode[]): void {
    const currentUrl = this.router.url.split('?')[0];
    // Resolve the most-specific matching route (drives highlighting always).
    this.activeRoutePath = this.resolveActiveRoutePath(nodes, currentUrl);

    // Accordion adjustment runs ONLY when the active route actually changed
    // (a real navigation to a different menu). This opens the active route's
    // section and closes the previously-active one — but it does NOT touch the
    // expansion state on plain re-renders, so a section the user manually opened
    // to browse stays open until they navigate elsewhere.
    if (this.activeRoutePath !== this.previousActiveRoutePath) {
      // Close the section that held the PREVIOUS active route (if its section is
      // different from the new one), then open the new active route's section.
      // Sections the user manually opened are left untouched.
      this.collapseSectionOf(nodes, this.previousActiveRoutePath);
      this.previousActiveRoutePath = this.activeRoutePath;
      if (this.activeRoutePath) this.markActive(nodes, currentUrl);
    }
  }

  /**
   * Collapse the top-level section (and inner parents) that contains the leaf
   * with the given routePath. Used to close the previously-active section when
   * navigating to a menu in a different section.
   */
  private collapseSectionOf(nodes: MenuNode[], routePath: string | null): void {
    if (!routePath) return;
    const collapseIfContains = (node: MenuNode): boolean => {
      if (node.children.length === 0) {
        return this.normalizePath(node.routePath) === routePath;
      }
      const contains = node.children.some(collapseIfContains);
      if (contains) node.expanded = false;
      return contains;
    };
    for (const n of nodes) collapseIfContains(n);
  }

  /**
   * Find the menu route that best matches the URL: the longest routePath that
   * equals the URL or is a segment-boundary prefix of it. Returns null when no
   * route matches. This makes /employees/details-import resolve to the Details
   * Import leaf instead of also matching the shorter /employees (Employee
   * Master), which previously highlighted two menus at once.
   */
  private resolveActiveRoutePath(nodes: MenuNode[], url: string): string | null {
    let best: string | null = null;
    const walk = (list: MenuNode[]): void => {
      for (const node of list) {
        const path = this.normalizePath(node.routePath);
        if (path && (url === path || url.startsWith(path + '/'))) {
          if (best === null || path.length > best.length) best = path;
        }
        if (node.children.length) walk(node.children);
      }
    };
    walk(nodes);
    return best;
  }

  private normalizePath(routePath: string | null | undefined): string {
    if (!routePath) return '';
    return routePath.split('?')[0].replace(/\/+$/, '');
  }

  /**
   * DFS: returns true if this node or any descendant is active.
   * Expands parent nodes whose children contain the active route.
   */
  private markActive(nodes: MenuNode[], _currentUrl: string): boolean {
    let anyActive = false;
    for (const node of nodes) {
      const selfActive = this.isLeafActive(node);
      const childActive = node.children.length > 0
        ? this.markActive(node.children, _currentUrl)
        : false;
      if (node.children.length > 0 && childActive) {
        // Open the section that contains the active route. Other sections are
        // left as-is (only the previously-active section is closed elsewhere),
        // so manually-opened sections are not disturbed.
        node.expanded = true;
      }
      if (selfActive || childActive) anyActive = true;
    }
    return anyActive;
  }

  // ── Template helpers ───────────────────────────────────────────────────────

  /** True when the node itself or any descendant is the resolved active route. */
  isActive(node: MenuNode): boolean {
    return this.nodeOrDescendantActive(node);
  }

  private nodeOrDescendantActive(node: MenuNode): boolean {
    if (this.isLeafActive(node)) return true;
    return node.children.some(c => this.nodeOrDescendantActive(c));
  }

  /** A node is active only when it owns the single most-specific matching route. */
  private isLeafActive(node: MenuNode): boolean {
    return !!this.activeRoutePath && this.normalizePath(node.routePath) === this.activeRoutePath;
  }

  /** Used by ngFor trackBy for performance. */
  trackByMenuId(_: number, node: MenuNode): string {
    return node.menuId;
  }
}
